import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Wand2, Save, Users, ListChecks } from "lucide-react";
import { toast } from "sonner";
import DrawGeneratorDialog from "@/components/DrawGeneratorDialog";

/**
 * /admin/groups — Season 3 group allocation (Team Portal §4).
 * Auto-allocate button (backend picks A/B/C from seed_map) + per-team dropdown
 * to manually override.
 */
const GROUPS = ["A", "B", "C"];
const GROUP_COLORS = { A: "#F4B400", B: "#00A86B", C: "#7DD3FC" };

export default function GroupsAdmin() {
  const [teams, setTeams] = useState([]);
  const [seedPreview, setSeedPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState({});
  const [drawsOpen, setDrawsOpen] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/teams");
      setTeams(data);
    } catch (e) {
      toast.error("Failed to load teams");
    } finally { setBusy(false); }
  };

  const previewSeeds = async () => {
    try {
      const { data } = await api.post("/tournament/allocate-groups");
      setSeedPreview(data);
    } catch (_) { /* silent */ }
  };

  useEffect(() => { load(); previewSeeds(); }, []);

  const grouped = useMemo(() => {
    const g = { A: [], B: [], C: [], unassigned: [] };
    teams.forEach(t => {
      if (GROUPS.includes(t.group)) g[t.group].push(t);
      else g.unassigned.push(t);
    });
    return g;
  }, [teams]);

  const autoAllocate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tournament/apply-groups");
      toast.success(`Auto-allocated ${data.written} teams into A / B / C`);
      await load();
      await previewSeeds();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-allocation failed");
    } finally { setBusy(false); }
  };

  const setGroup = async (teamId, group) => {
    setSaving(s => ({ ...s, [teamId]: true }));
    try {
      await api.put(`/teams/${teamId}/group`, { group: group || null });
      setTeams(ts => ts.map(t => t.id === teamId ? { ...t, group: group || null } : t));
      toast.success("Group updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(s => ({ ...s, [teamId]: false }));
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="groups-admin">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">Season 3 · Tournament</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white mt-1 leading-none">
            Round-Robin<br/><span className="text-[#F4B400]">Groups.</span>
          </h1>
          <div className="mt-3 text-zinc-400 text-sm max-w-2xl">
            Auto-allocate up to <b className="text-white">13 teams</b> into <b className="text-white">Group A (4)</b>, <b className="text-white">Group B (4)</b> and <b className="text-white">Group C (5)</b> per §4 (Seeds 1,6,7,12 / 2,5,8,11 / 3,4,9,10,13). Or drag any team to a different group manually.
          </div>
        </div>
        <Button
          onClick={autoAllocate}
          disabled={busy}
          className="bg-[#F4B400] text-black font-bold uppercase tracking-widest hover:bg-[#e5aa00]"
          data-testid="auto-allocate-btn"
        >
          <Wand2 className="w-4 h-4 mr-2" /> Auto-Allocate
        </Button>
      </div>

      {/* Create Draws CTA — visible once any group has ≥2 teams */}
      {(grouped.A.length >= 2 || grouped.B.length >= 2 || grouped.C.length >= 2) && (
        <div className="mb-6 p-4 rounded-xl border border-[#F4B400]/30 bg-gradient-to-r from-[#1a1408] via-[#0f1a15] to-[#0f1a15] flex items-center justify-between gap-4 flex-wrap" data-testid="create-draws-cta">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">Step 2 · After groups are set</div>
            <div className="font-anton text-2xl uppercase tracking-tight text-white mt-0.5">Create Round-Robin Draws</div>
            <div className="text-xs text-zinc-400 mt-1 max-w-xl">Configure days (e.g. Thu/Fri/Sat) with matches per team per day. System auto-generates every group tie and reserves QF/SF/Final slots. Edit times, seeds and courts later on the Schedule page.</div>
          </div>
          <Button onClick={() => setDrawsOpen(true)} className="bg-emerald-500 text-black font-bold uppercase tracking-widest hover:bg-emerald-400" data-testid="open-draws-btn">
            <ListChecks className="w-4 h-4 mr-2" /> Create Draws
          </Button>
        </div>
      )}

      {/* 3 group columns */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {GROUPS.map(g => (
          <Card key={g} className="p-5 bg-[#0f1a15] border-white/10" data-testid={`group-col-${g}`} style={{ borderTop: `4px solid ${GROUP_COLORS[g]}` }}>
            <div className="flex items-baseline justify-between mb-3">
              <div className="font-anton text-3xl uppercase tracking-tight text-white">Group {g}</div>
              <div className="text-xs text-zinc-500">{grouped[g].length} teams</div>
            </div>
            <div className="space-y-2">
              {grouped[g].length === 0 && (
                <div className="text-xs text-zinc-600 italic py-6 text-center">No teams in this group</div>
              )}
              {grouped[g].map(t => <TeamRow key={t.id} team={t} onGroupChange={setGroup} saving={saving[t.id]} />)}
            </div>
          </Card>
        ))}
      </div>

      {/* Unassigned */}
      {grouped.unassigned.length > 0 && (
        <Card className="p-5 bg-[#0f1a15] border-white/10 mb-6" data-testid="group-col-unassigned">
          <div className="flex items-baseline justify-between mb-3">
            <div className="font-anton text-2xl uppercase tracking-tight text-zinc-400">Unassigned</div>
            <div className="text-xs text-zinc-500">{grouped.unassigned.length} teams — click Auto-Allocate to place them</div>
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            {grouped.unassigned.map(t => <TeamRow key={t.id} team={t} onGroupChange={setGroup} saving={saving[t.id]} />)}
          </div>
        </Card>
      )}

      {/* Seed preview */}
      {seedPreview?.seeds && seedPreview.seeds.length > 0 && (
        <Card className="p-5 bg-[#0f1a15] border-white/10" data-testid="seed-preview">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-[#F4B400]" />
            <div className="font-anton text-2xl uppercase tracking-tight text-white">Seed Preview</div>
            <div className="text-xs text-zinc-500 ml-2">from current player rankings (lowest total = Seed 1)</div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {seedPreview.seeds.map(s => (
              <div key={s.team_id} className="p-2 rounded bg-[#11241b] border border-white/10 flex items-center gap-2">
                <span className="w-8 text-center font-anton text-lg text-[#F4B400]">{s.seed}</span>
                <span className="text-sm text-white truncate flex-1">{s.team_name}</span>
                <span className="text-[10px] font-mono text-zinc-400">Σ={s.ranking_total ?? "—"}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
      <DrawGeneratorDialog
        open={drawsOpen}
        onOpenChange={setDrawsOpen}
        groupsSummary={{ A: grouped.A.length, B: grouped.B.length, C: grouped.C.length }}
      />
    </div>
  );
}

function TeamRow({ team, onGroupChange, saving }) {
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-[#11241b] border border-white/10" data-testid={`team-row-${team.id}`}>
      <div className="w-8 h-8 rounded bg-black/30 overflow-hidden shrink-0">
        {team.logo_url && <img src={team.logo_url} alt={team.name} className="w-full h-full object-contain" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white truncate">{team.name}</div>
        <div className="text-[10px] text-zinc-500">{team.players?.length || 0} players</div>
      </div>
      <select
        value={team.group || ""}
        onChange={(e) => onGroupChange(team.id, e.target.value)}
        disabled={saving}
        data-testid={`group-select-${team.id}`}
        className="bg-[#060d09] border border-white/10 text-white text-xs px-2 py-1 rounded"
      >
        <option value="">—</option>
        <option value="A">Group A</option>
        <option value="B">Group B</option>
        <option value="C">Group C</option>
      </select>
    </div>
  );
}
